/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// ACTIVITIES THE CLIENT STATES AGAINST THE REGULATOR RECORD (v1_983). Until now
// the comparison was left to the models: Alex was told to compare (v1_875) and
// the interview inconsistency extractor flagged a gap only once Alex had raised
// it and the client had explained. In 21 of 167 walk interviews Alex never
// raised "we arrange deals in investments" against an FCA record of advising
// only, and in 8 it told the client the permissions covered it (w1ad4d562:
// "That fits well with everything you've described ... arranging deals in
// investments"). The officer's business description then read "This matches
// the FCA Financial Services Register extract".
//
// This compares in code. The record is the permissions text the board read from
// the regulator document (regulatoryStatus.licenceType); the statements are the
// client's own words (its chat turns, its application). Every activity the
// client states, in a clause that does not deny it, that the record does not
// list is a gap: a point for Alex, a risk factor for the officer, and a guard on
// the business description. Pure; nothing the client or officer reads carries a
// dash (standing rule 6).

const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const noDash = (s) => clean(s).replace(/\s*[‐-―]\s*/g, ', ').replace(/\s+-\s+/g, ', ').replace(/(\w)-(\w)/g, '$1 $2');

// The regulated activities compared. stated: the client says it does this.
// listed: the record shows a permission for it. A record that lists none of
// these (an empty or non FCA style text) still lets the client money rule run.
export const ACTIVITIES = [
  { key: 'arranging', mention: /\barrang(?:e|es|ed|ing)\b/i, label: 'arranging deals in investments',
    stated: /\barrang(?:e|es|ed|ing)\s+(?:the\s+)?(?:deals?|transactions?|trades?|investments?)\b|\b(?:we|firm|company)\s+(?:also\s+)?arrange\b(?![^.;]{0,30}\b(?:insurance|mortgages?|loans?|credit)\b)/i,
    listed: /arranging\s*(?:\(bringing about\)\s*)?deals\s+in\s+investments|making\s+arrangements\s+with\s+a\s+view\s+to\s+transactions/i },
  { key: 'dealing_agent', mention: /\bas\s+(?:an\s+)?agent\b|\bexecut/i, label: 'dealing in investments as agent',
    stated: /\bdeal(?:s|ing)?\s+(?:in\s+investments\s+)?as\s+(?:an\s+)?agent\b|\bexecut(?:e|es|ing)\s+(?:trades|orders|transactions)\s+(?:for|on\s+behalf\s+of)\s+(?:our\s+)?clients\b/i,
    listed: /dealing\s+in\s+investments\s+as\s+agent/i },
  { key: 'dealing_principal', mention: /\bas\s+principal\b|\bproprietary\b|\bown\s+account\b/i, label: 'dealing in investments as principal',
    stated: /\bdeal(?:s|ing)?\s+(?:in\s+investments\s+)?as\s+principal\b|\bproprietary\s+trading\b|\btrade\s+on\s+(?:our|its)\s+own\s+account\b/i,
    listed: /dealing\s+in\s+investments\s+as\s+principal/i },
  { key: 'managing', mention: /\bmanag(?:e|es|ing|ement)\b|\bdiscretionary\b/i, label: 'managing investments',
    stated: /\bmanag(?:e|es|ing)\s+(?:client\s+)?(?:investments|portfolios|assets)\b|\bdiscretionary\s+(?:management|portfolio|basis|mandates?)\b|\bportfolio\s+management\b/i,
    listed: /managing\s+investments|managing\s+an?\s+(?:AIF|UCITS)/i },
  { key: 'safeguarding', mention: /\bsafeguard|\bcustody\b|\bclient\s+assets\b/i, label: 'safeguarding and administering investments (custody)',
    stated: /\b(?:we|I|the\s+firm|the\s+company)\s+(?:also\s+)?(?:safeguard|hold|holds|keep|keeps)\s+(?:and\s+administer\s+)?(?:client|customer)s?'?\s+(?:assets|investments|securities)\b|\b(?:we|the\s+firm)\s+(?:also\s+)?provide\s+custody\b/i,
    listed: /safeguarding\s+and\s+administ/i },
  { key: 'client_money', mention: /\bclient\s+money\b|\bclient\s+funds\b/i, label: 'holding client money',
    stated: /\b(?:we|I|the\s+firm|the\s+company)\s+(?:also\s+|do\s+|will\s+)?(?:hold|holds|control|controls|receive|receives)\s+(?:client|customer)s?'?\s+(?:money|funds|cash)\b|\b(?:our|a)\s+client\s+money\s+account\b/i,
    // Listed unless the record carries the standard requirement against it.
    listed: null, deniedBy: /may\s+not\s+hold\s+or\s+control\s+client\s+money|not\s+(?:permitted|allowed)\s+to\s+hold\s+client\s+money/i },
  { key: 'credit_broking', mention: /\bcredit\b/i, label: 'credit broking',
    stated: /\bcredit\s+brok(?:er|ers|ing)\b|\barrang(?:e|es|ing)\s+(?:loans|credit|finance)\s+for\b/i,
    listed: /credit\s+broking/i },
  { key: 'insurance', mention: /\binsurance\b/i, label: 'insurance distribution',
    stated: /\binsurance\s+(?:distribution|mediation|broking|broker)\b|\b(?:sell|sells|selling|arrang(?:e|es|ing))\s+(?:general\s+|life\s+)?insurance\b/i,
    listed: /insurance\s+distribution|insurance\s+mediation|contracts?\s+of\s+insurance/i },
  { key: 'mortgages', mention: /\bmortgage/i, label: 'mortgage advice or arranging',
    stated: /\bmortgage\s+(?:advice|advis(?:er|ing|ory)|brok(?:er|ing))\b|\b(?:arrang|advis)(?:e|es|ing)\s+(?:on\s+)?mortgages\b/i,
    listed: /regulated\s+mortgage|home\s+finance/i },
];

// A clause that denies the activity ("we do not arrange deals today",
// "arranging is not on our permissions yet", "no discretionary management")
// states nothing. Clauses split at full stops, semicolons and "but".
const NEGATION = /\b(?:not|no|never|don'?t|doesn'?t|isn'?t|aren'?t|won'?t|without|nor|neither|no\s+longer|yet\s+to|rather\s+than|instead\s+of|other\s+than|apart\s+from|except)\b/i;
// A denial that follows the activity it names ("Managing investments is not on
// our permissions", "portfolio management isn't something we offer", "managing
// investments, which we do not do", "Managing investments: not permitted").
// Walk we8dd8d41 (16/10/2026): the client wrote "Managing investments is not on
// our permissions, and the pending variation does not add it either", the check
// read only the words before the activity, and the denial became a stated
// activity: a point Alex was told to raise, a SUBMIT hold, and a High risk
// factor for the officer. Only a denial attached to the activity itself counts,
// so "we manage client portfolios and do not hold client money" still states it.
// "not only" and "not just" are not denials: "Managing investments is not only
// our core business but also..." and "isn't just a side line" still state it.
const NEGATED_AFTER = /^\s*(?:\([^)]*\)\s*)?(?:(?:is|are|was|were)\s+(?:also\s+)?(?:not|never|no\s+longer)\b(?!\s+(?:only|just)\b)|(?:has|have)\s+(?:never|not)\b(?!\s+(?:only|just)\b)|(?:isn|aren|wasn|weren)['’]?t\b(?!\s+(?:only|just)\b)|(?:is|are)\s+(?:outside|excluded\s+from)\b|,?\s*which\s+(?:we|I|the\s+firm|the\s+company)\s+(?:do|does|did)\s*(?:not\b|n['’]t\b)(?!\s+(?:only|just)\b)|:\s*(?:not|no|none)\b(?!\s+(?:only|just)\b))/i;
function clauses(text) {
  return clean(text).split(/(?<=[.!?;])\s+|\s*;\s*|,?\s+\bbut\b\s+/i).map(clean).filter(Boolean);
}

// Every activity the statements state, with the first clause that states it.
export function statedActivities(statements) {
  const found = new Map();
  for (const st of (Array.isArray(statements) ? statements : [])) {
    const text = typeof st === 'string' ? st : st && st.text;
    const source = (st && st.source) || 'client';
    for (const c of clauses(text)) {
      for (const a of ACTIVITIES) {
        if (found.has(a.key)) continue;
        const m = c.match(a.stated);
        if (!m) continue;
        if (NEGATION.test(c.slice(0, m.index + m[0].length))) continue;
        if (NEGATED_AFTER.test(c.slice(m.index + m[0].length))) continue;
        found.set(a.key, { key: a.key, label: a.label, evidence: noDash(c).slice(0, 240), source });
      }
    }
  }
  return [...found.values()];
}

// The record to compare against, or null when there is none.
export function permissionRecord(report) {
  const rs = report && report.regulatoryStatus;
  if (!rs || rs.regulated !== true) return null;
  const text = clean(rs.licenceType);
  if (!text) return null;
  return { regulator: noDash(rs.regulator) || 'the regulator', number: clean(rs.licenceNumber), text, shown: noDash(text).replace(/^authorised firm\s*,?\s*/i, '') };
}

// The gaps: stated activities the record does not list.
export function permissionGaps(report, statements) {
  const rec = permissionRecord(report);
  if (!rec) return [];
  return statedActivities(statements).filter(s => {
    const a = ACTIVITIES.find(x => x.key === s.key);
    if (a.listed === null) return a.deniedBy.test(rec.text);
    return !a.listed.test(rec.text);
  });
}

// The client's own words from a transcript ("Client: ..." turns) or messages.
export function clientStatements(input) {
  if (Array.isArray(input)) {
    return input.filter(m => m && (m.role === 'user' || m.role === 'client')).map(m => ({ text: typeof m.content === 'string' ? m.content : (m.text || ''), source: 'client' }));
  }
  return String(input || '').split(/\n\s*\n/).filter(t => /^Client:\s*/.test(t)).map(t => ({ text: t.replace(/^Client:\s*/, ''), source: 'client' }));
}

// The officer's risk factor for each gap. Replaced on every rebuild (flag key).
export function permissionGapFactors(gaps, report) {
  const rec = permissionRecord(report);
  if (!rec) return [];
  return gaps.map(g => ({
    factor: 'Activity not shown on the ' + rec.regulator + ' record: ' + g.label,
    rating: 'High',
    notes: 'The client described ' + g.label + ' ("' + g.evidence + '"). The ' + rec.regulator + ' record on file'
      + (rec.number ? ' (reference ' + rec.number + ')' : '') + ' shows: ' + rec.shown + '. ' + capital(g.label) + ' is not among them.'
      + ' Record how the activity is carried on (under another firm\'s permission, as an appointed representative, under a pending application, or not at all) before sign off.',
    permissionGap: true,
    activity: g.key,
  }));
}
const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// The point Alex raises for each gap: neutral, no judgement, no dash.
export function permissionGapQuestions(gaps, report) {
  const rec = permissionRecord(report);
  if (!rec) return [];
  return gaps.map(g => 'You mentioned ' + g.label + '. The ' + rec.regulator + ' register record we hold for the firm shows: ' + rec.shown
    + '. Could you tell us how that activity is carried on, for example under another firm\'s permission, as an appointed representative, under a pending application, or not at present?');
}

// Whether Alex has already put a gap to the client: a sentence of an Alex turn
// that names the activity and either says the record does not show it ("I
// don't see arranging as a permission on that extract") or asks how it is
// carried on ("how that arranging activity fits with the permissions?"). A
// statement that the record matches or covers it is never a raise, so "That
// fits well with ... arranging deals in investments" (w1ad4d562) and "which
// matches the permissions on your FCA extract" (wfda094a0) do not count.
const AFFIRM_SENTENCE = /\b(?:match(?:es|ing)?|fits?\s+(?:well|with|within|comfortably)|sits?\s+comfortably|consistent\s+with|in\s+line\s+with|covers?|covered|confirms?|records?\s+advising)\b/i;
const SAYS_NOT_SHOWN = /\b(?:don'?t|do\s+not|doesn'?t|does\s+not|isn'?t|is\s+not|aren'?t|not|no)\b[^.?!]{0,60}\b(?:see|show|shows|shown|list|lists|listed|include|includes|included|cover|covers|covered|appear|appears|feature|features|permission)\b/i;
// A4 (11/10/2026): "how that arranging activity is covered in the firm's permissions" (wec36d783) is a raise too.
const ASKS_HOW = /\bhow\b[^.?!]{0,100}\b(?:fits?|carried\s+(?:on|out)|works?|operates?|sits?|done|structured|happens|covered|covers|permitted|authori[sz]ed|falls?\s+(?:under|within)|sits?\s+under|done\s+under)\b|\bbasis\s+on\s+which\b|\bon\s+what\s+basis\b|\bunder\s+(?:what|which)\s+(?:permission|authorisation|basis)\b/i;
export function gapRaised(messages, gap) {
  const a = ACTIVITIES.find(x => x.key === gap.key);
  return (Array.isArray(messages) ? messages : []).some(m => {
    if (!m || m.role !== 'assistant') return false;
    const text = String(m.content || '');
    if (!/\b(?:record|register|extract|permission|permissions|authorisation)\b/i.test(text)) return false;
    return text.split(/(?<=[.?!])\s+/).some(sen => {
      if (!a.mention.test(sen)) return false;
      const question = /\?\s*$/.test(sen);
      if (!question && AFFIRM_SENTENCE.test(sen)) return false;
      // "shows ... advising on investments and arranging deals" lists it as held.
      if (new RegExp('\\b(?:shows?|lists?|records?|confirms?|holds?)\\b[^.?!]{0,100}\\band\\s+(?:on\\s+)?(?:' + a.mention.source + ')', 'i').test(sen)) return false;
      return SAYS_NOT_SHOWN.test(sen) || ASKS_HOW.test(sen);
    });
  });
}

// Whether the client has already addressed a gap in its own words: a later
// client turn that names the activity together with its permission position
// ("Arranging is not on our permissions yet. We applied for a variation of
// permission in June 2026"). Then the point is settled and is not pressed.
export function gapAnswered(messages, gap) {
  const a = ACTIVITIES.find(x => x.key === gap.key);
  const list = Array.isArray(messages) ? messages : [];
  const firstStated = list.findIndex(m => m && m.role === 'user' && statedActivities([String(m.content || '')]).some(s => s.key === gap.key));
  return list.some((m, i) => i > firstStated && firstStated >= 0 && m && m.role === 'user' && a.mention.test(String(m.content || ''))
    && /\b(?:permission|permissions|variation|authoris\w*|appointed\s+representative|regulator|FCA|register)\b/i.test(String(m.content || '')));
}

// ALEX: the standing rule, on every path and every visit.
export const NEVER_AFFIRM_COVERAGE = '\n\nPERMISSION COVERAGE, NEVER AFFIRM: never tell the client that their permissions, licence, authorisation or register record cover, include, allow, fit, match or are consistent with an activity unless the record on file lists that activity by name. If you do not hold the record, or it does not list the activity, say nothing about whether it is covered. Whether an activity is permitted is for the compliance team to decide, never for you.';

// THE REPORT PROMPTS (interview submit and both officer rebuilds).
export const REPORT_DESCRIPTION_RULE = '\n\nBUSINESS DESCRIPTION AND PERMISSIONS: describe the regulator permissions only as the regulator record states them. Never write that the business, or an activity the client described, matches, fits, is consistent with or is covered by the record or the permissions unless the record lists that activity by name; an activity the client described that the record does not list is stated as described by the client and not shown on the record.';

// ALEX: the gaps not yet raised, as a point to raise now.
// The gaps the next reply is told to raise: stated, not answered, and not yet
// raised. Raised is the server's record (collected_data.openPointLedger, A34:
// the turn that told Alex to raise it, or a SUBMIT held for it) or Alex's
// wording naming it; the record holds when Alex rewords the ask.
export function gapsToRaise(gaps, messages, ledger = []) {
  const recorded = new Set((Array.isArray(ledger) ? ledger : []).filter(e => e && typeof e.key === 'string').map(e => e.key));
  return (gaps || []).filter(g => !recorded.has(g.key) && !gapRaised(messages, g) && !gapAnswered(messages, g));
}

export function permissionGapClause(gaps, report, messages, ledger = []) {
  const rec = permissionRecord(report);
  if (!rec) return '';
  const open = gapsToRaise(gaps, messages, ledger);
  if (!open.length) return '';
  return '\n\nACTIVITY NOT SHOWN ON THE REGULATOR RECORD, RAISE IT IN YOUR NEXT REPLY: the client has described '
    + open.map(g => g.label).join(' and ') + '. The ' + rec.regulator + ' record on file shows only: ' + rec.shown + '.'
    + ' In your next reply, before any other point, say plainly and without accusation what they described, say that the register record you hold does not show a permission for it, and ask how that activity is carried on (for example under another firm\'s permission, as an appointed representative, under a pending application, or not at present).'
    + ' Ask it ONCE. Accept their answer, do not raise it again, and do not say whether the answer is acceptable.';
}

// THE OFFICER'S BUSINESS DESCRIPTION. With a gap, no sentence may say the
// description matches or is covered by the record, and the gap is stated.
const AFFIRMS = /\b(?:match(?:es|ing)?|consistent\s+with|in\s+line\s+with|aligns?\s+with|fits?\s+(?:with|within)|covered\s+by|within\s+(?:the|its|their)\s+(?:permissions?|authorisation|scope))\b[^.]{0,120}\b(?:register|extract|record|permissions?|authorisation|FCA|regulator)\b|\b(?:register|extract|record|permissions?)\b[^.]{0,60}\b(?:confirms?|covers?|shows?)\b[^.]{0,60}\b(?:arrang|manag|deal|custody|safeguard|client\s+money|insurance|mortgage|credit)/i;
export function guardDescription(text, gaps, report) {
  const rec = permissionRecord(report);
  const t = clean(text);
  if (!rec || !gaps.length || !t) return text;
  const marker = 'Not shown on the ' + rec.regulator + ' record:';
  const recordLine = 'The ' + rec.regulator + ' record on file shows: ' + rec.shown + '.';
  const kept = t.split(/(?<=\.)\s+/).filter(sen => !sen.startsWith(marker) && sen !== recordLine)
    .map(sen => (AFFIRMS.test(sen) ? recordLine : sen));
  if (!kept.includes(recordLine)) kept.push(recordLine);
  return clean([...new Set(kept)].join(' ') + ' ' + marker + ' ' + gaps.map(g => g.label).join(', ') + ' (described by the client; the record does not list it).');
}

// One call for the three generation paths: the factors replaced, the
// description guarded, the gaps recorded on the report for the fact pack.
export function applyPermissionGaps(report, statements) {
  if (!report || typeof report !== 'object') return { report, gaps: [] };
  const gaps = permissionGaps(report, statements);
  report.riskFactors = (Array.isArray(report.riskFactors) ? report.riskFactors : []).filter(rf => !(rf && rf.permissionGap));
  report.riskFactors.push(...permissionGapFactors(gaps, report));
  report.permissionGaps = gaps;
  if (gaps.length && report.companyDetails && typeof report.companyDetails === 'object' && report.companyDetails.businessDescription) {
    report.companyDetails.businessDescription = guardDescription(report.companyDetails.businessDescription, gaps, report);
  }
  return { report, gaps };
}
